import {
  KeyboardEvent,
  RefObject,
  useCallback,
  useLayoutEffect,
  useState,
} from 'react';

import { PromptText } from './CliTerminalText';

interface CliTerminalInputProps {
  inputRef: RefObject<HTMLInputElement>;
  prompt: string;
  value: string;
  placeholder: string;
  busy: boolean;
  onChange(value: string): void;
  onKeyDown(event: KeyboardEvent<HTMLInputElement>): void;
}

interface Cursor {
  at: number;
  selecting: boolean;
  scroll: number;
}

/**
 * The prompt line. A native input keeps editing, IME and paste working; the caret it would draw is hidden
 * and a block cursor is painted over the same text instead, solid while focused and hollow when not.
 */
function CliTerminalInput({
  inputRef,
  prompt,
  value,
  placeholder,
  busy,
  onChange,
  onKeyDown,
}: Readonly<CliTerminalInputProps>) {
  const [cursor, setCursor] = useState<Cursor>({
    at: 0,
    selecting: false,
    scroll: 0,
  });
  const [focused, setFocused] = useState(false);

  const syncCursor = useCallback(() => {
    const field = inputRef.current;
    if (!field) return;
    const start = field.selectionStart ?? field.value.length;
    setCursor({
      at: start,
      selecting: start !== field.selectionEnd,
      scroll: field.scrollLeft,
    });
  }, [inputRef]);

  // History and completion replace the value from outside, which moves the caret without a select event.
  useLayoutEffect(syncCursor, [value, syncCursor]);

  // Over an empty field the block sits on the placeholder's first letter, as a native caret would.
  const under = value[cursor.at] ?? placeholder[0] ?? ' ';
  const showCursor = !busy && !cursor.selecting;

  return (
    <div className="CliTerminal__input-row">
      <span className="CliTerminal__caret">
        <PromptText text={prompt} />
      </span>
      <span className="CliTerminal__field">
        <input
          ref={inputRef}
          className="CliTerminal__input"
          value={value}
          spellCheck={false}
          autoComplete="off"
          placeholder={placeholder}
          onChange={event => onChange(event.target.value)}
          onKeyDown={onKeyDown}
          onSelect={syncCursor}
          onScroll={syncCursor}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
        />
        {showCursor && (
          <span className="CliTerminal__cursor-track" aria-hidden>
            <span
              className="CliTerminal__cursor-line"
              style={{ transform: `translateX(-${cursor.scroll}px)` }}
            >
              <span className="CliTerminal__cursor-lead">
                {value.slice(0, cursor.at)}
              </span>
              {/* Keyed on the position so the blink restarts on every keystroke, as a terminal's does. */}
              <span
                key={`${cursor.at}:${value.length}`}
                className={`CliTerminal__cursor${focused ? '' : ' is-hollow'}`}
              >
                {under}
              </span>
            </span>
          </span>
        )}
      </span>
    </div>
  );
}

export default CliTerminalInput;
